import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

@Component({
  selector: 'app-pdf-reader',
  templateUrl: './pdf-reader.component.html',
  styleUrls: ['./pdf-reader.component.scss']
})
export class PdfReaderComponent implements AfterViewInit, OnDestroy {
  private _src: string;
  @Input()
  set src(value: string) {
    this._src = value;
    this.isFinished = false;
    this.isLoading = true;
  }

  get src(): string {
    return this._src
  }

  private _hasFullscreen: boolean = true;

  @Input()
  fileName: string = 'file.pdf';

  @Input()
  set hasFullscreen(value: boolean) {
    this._hasFullscreen = value;
  }

  get hasFullscreen(): boolean {
    return this._hasFullscreen
  }

  get hasController(): boolean {
    return this._hasFullscreen
  }

  @Output() finish = new EventEmitter<void>();

  isLoading: boolean;
  isFinished: boolean;
  isFullscreen: boolean;
  page: number;
  container: HTMLElement;
  printIframe: HTMLIFrameElement;
  objectUrl: string;
  isWaitingDownload = false;
  isWaitingPrint = false;
  constructor(private elRef: ElementRef, private http: HttpClient) { }


  ngAfterViewInit(): void {
    if (this.hasFullscreen) {
      document.addEventListener('fullscreenchange', this.onFullScreenChangeBound, false);
      document.addEventListener('MSFullscreenChange', this.onFullScreenChangeBound, false);
      document.addEventListener('mozfullscreenchange', this.onFullScreenChangeBound, false);
      document.addEventListener('webkitfullscreenchange', this.onFullScreenChangeBound, false);
      this.container = this.elRef.nativeElement.parentNode;
    }
  }

  onProgress(event: { loaded: number, total: number }) {
    this.isLoading = event.loaded != event.total;
  }

  onScroll(event: any) {
    if (this.isLoading) {
      return;
    }
    if (!this.isFinished) {
      this.isFinished = ((event.target.scrollTop + event.target.offsetHeight) / event.target.scrollHeight) > 0.95
      if (this.isFinished) {
        this.finish.emit();
      }

    }
  }

  //************************************************************************************
  // FULLSCREEN
  //************************************************************************************
  onFullScreenChangeBound = this.onFullScreenChange.bind(this)
  /**
   * handle on user exit by key esc
   * @param e 
   */
  onFullScreenChange(e: any) {
    if (!document.fullscreenElement && this.isFullscreen && e.target == this.container) {
      this.exitFullscreen();
    }
  };

  onFullscreenClick(/*e*/) {
    if (!this.isFullscreen) {
      this.enterFullscreen(this.container);
    } else {
      this.exitFullscreen();
    }
  };


  enterFullscreen(element) {
    const docElmWithBrowsersFullScreenFunctions = element as HTMLElement & {
      mozRequestFullScreen(): Promise<void>;
      webkitRequestFullscreen(): Promise<void>;
      msRequestFullscreen(): Promise<void>;
    };
    let promise: Promise<void>;
    if (docElmWithBrowsersFullScreenFunctions.requestFullscreen) {
      promise = docElmWithBrowsersFullScreenFunctions.requestFullscreen();
    } else if (docElmWithBrowsersFullScreenFunctions.msRequestFullscreen) {
      docElmWithBrowsersFullScreenFunctions.msRequestFullscreen();
    } else if (docElmWithBrowsersFullScreenFunctions.mozRequestFullScreen) {
      promise = docElmWithBrowsersFullScreenFunctions.mozRequestFullScreen();
    } else {
      promise = docElmWithBrowsersFullScreenFunctions.webkitRequestFullscreen();
    }
    promise.finally(() => {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 200);
    })

    this.isFullscreen = true;
  };

  exitFullscreen() {
    const docWithBrowsersExitFunctions = document as Document & {
      mozCancelFullScreen(): Promise<void>;
      webkitExitFullscreen(): Promise<void>;
      msExitFullscreen(): Promise<void>;
    };
    if (docWithBrowsersExitFunctions.fullscreenElement) {
      if (docWithBrowsersExitFunctions.exitFullscreen) {
        docWithBrowsersExitFunctions.exitFullscreen();
      } else if (docWithBrowsersExitFunctions.mozCancelFullScreen) { /* Firefox */
        docWithBrowsersExitFunctions.mozCancelFullScreen();
      } else if (docWithBrowsersExitFunctions.webkitExitFullscreen) { /* Chrome, Safari and Opera */
        docWithBrowsersExitFunctions.webkitExitFullscreen();
      } else if (docWithBrowsersExitFunctions.msExitFullscreen) { /* IE/Edge */
        docWithBrowsersExitFunctions.msExitFullscreen();
      }
    }
    this.isFullscreen = false;
  };

  onDownload() {
    this.isWaitingDownload = true;
    if (this.objectUrl) {
      const a = document.createElement('a')
      a.href = this.objectUrl
      a.download = this.fileName;
      a.click();
      this.isWaitingDownload = false;
    } else {
      this.http.get(this.src, {
        responseType: 'blob'
      }).subscribe(
        blob => {
          blob = new Blob([blob], { type: 'application/pdf' }); // blob = new Blob([blob], { type: "octet/stream"}), //for firfox
          const a = document.createElement('a')
          this.objectUrl = URL.createObjectURL(blob)
          a.href = this.objectUrl
          a.download = this.fileName;
          a.click();
          this.isWaitingDownload = false;
        },
        err => {
          this.isWaitingDownload = false;
        })
    }

  }

  onPrint() {
    this.isWaitingPrint = true;
    if (this.printIframe) {
      this.printIframe.contentWindow.print();
      this.isWaitingPrint = false;
    } else if (this.objectUrl) {
      this.printIframe = document.createElement('iframe');
      document.body.appendChild(this.printIframe);
      this.printIframe.onload = () => {
        this.printIframe.contentWindow.print();
        this.isWaitingPrint = false;
      };
      this.printIframe.style.display = 'none';
      this.printIframe.src = this.objectUrl;
    } else {
      this.http.get(this.src, {
        responseType: 'blob'
      }).subscribe(
        blob => {
          blob = new Blob([blob], { type: 'application/pdf' });
          this.objectUrl = URL.createObjectURL(blob);
          this.printIframe = document.createElement('iframe');
          document.body.appendChild(this.printIframe);
          this.printIframe.onload = () => {
            this.printIframe.contentWindow.print();
            this.isWaitingPrint = false;
          };
          this.printIframe.style.display = 'none';
          this.printIframe.src = this.objectUrl;
        },
        err => {
          this.isWaitingPrint = false;
        })
    }

  }

  ngOnDestroy(): void {
    if (this.hasFullscreen) {
      document.removeEventListener('fullscreenchange', this.onFullScreenChangeBound);
      document.removeEventListener('MSFullscreenChange', this.onFullScreenChangeBound);
      document.removeEventListener('mozfullscreenchange', this.onFullScreenChangeBound);
      document.removeEventListener('webkitfullscreenchange', this.onFullScreenChangeBound);
    }
    if (this.printIframe) {
      this.printIframe.remove();
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }
}
